import React, { useRef, useEffect, useState } from 'react';

interface ProductShowcaseVideoProps {
  videoSrc: string;
  posterSrc: string;
  alt: string;
}

const ProductShowcaseVideo: React.FC<ProductShowcaseVideoProps> = ({ videoSrc, posterSrc, alt }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;

    if (!video || !container) return;

    // Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isInView) {
            setIsInView(true);
            
            // Start loading the video
            if (video.readyState === 0) {
              video.load();
            }
          }
        });
      },
      {
        rootMargin: '50px', // Start loading slightly before entering viewport
        threshold: 0.1,
      }
    );

    observer.observe(container);

    // Video event listeners
    const handleCanPlay = () => {
      // Auto-play when video can play
      video.play().catch((error) => {
        console.log('Autoplay prevented:', error);
      });
    };

    const handleLoadedData = () => {
      // Video data loaded
    };

    if (isInView) {
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('loadeddata', handleLoadedData);
    }

    return () => {
      observer.disconnect();
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, [isInView]);

  return (
    <div 
      ref={containerRef}
      className="relative w-full"
    >
      {/* Video element */}
      <video
        ref={videoRef}
        className="w-full h-auto rounded-lg shadow-2xl"
        poster={posterSrc}
        muted
        loop
        playsInline
        preload="none"
        aria-label={alt}
        style={{
          // Prevent layout shift
          aspectRatio: '16 / 9',
          width: '100%',
          height: 'auto',
          display: 'block'
        }}
      >
        {isInView && <source src={videoSrc} type="video/webm" />}
        {/* Fallback for browsers that don't support video */}
        <img 
          src={posterSrc} 
          alt={alt}
          className="w-full h-auto rounded-lg"
        />
      </video>
    </div>
  );
};

export default ProductShowcaseVideo;
